const getSpeechRecognitionConstructor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const getSafeSpeechRecognitionErrorMessage = (errorCode) => {
  if (errorCode === "not-allowed" || errorCode === "service-not-allowed") {
    return "Voice input needs microphone permission.";
  }

  return "Voice input is not available right now.";
};

export const speechRecognitionService = {
  isSupported() {
    return getSpeechRecognitionConstructor() != null;
  },

  createSession({
    lang,
    continuous,
    interimResults,
    onTranscript,
    onListeningChange,
    onError
  }) {
    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) {
      throw new Error("Speech recognition is not supported.");
    }

    const recognition = new Recognition();
    let finalTranscript = "";

    recognition.lang = lang;
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.onstart = () => onListeningChange?.(true);
    recognition.onend = () => onListeningChange?.(false);
    recognition.onerror = (event) => {
      const errorCode = event?.error;
      onError?.(getSafeSpeechRecognitionErrorMessage(errorCode), errorCode);
      onListeningChange?.(false);
    };
    recognition.onresult = (event) => {
      const results = event?.results;
      if (!results) return;

      let nextInterimTranscript = "";
      const startIndex = Number.isInteger(event?.resultIndex) ? Number(event.resultIndex) : 0;
      for (let index = startIndex; index < results.length; index += 1) {
        const result = results[index];
        const transcript = String(result?.[0]?.transcript ?? "").trim();
        if (!transcript) continue;

        if (result?.isFinal) {
          finalTranscript = [finalTranscript.trim(), transcript].filter(Boolean).join(" ");
        } else {
          nextInterimTranscript = [nextInterimTranscript.trim(), transcript].filter(Boolean).join(" ");
        }
      }

      onTranscript?.({
        finalTranscript,
        interimTranscript: nextInterimTranscript
      });
    };

    return {
      recognition,
      start: () => recognition.start(),
      stop: () => recognition.stop(),
      abort: () => recognition.abort()
    };
  }
};

export default speechRecognitionService;
